import { useState, useEffect } from 'react';
import { getAllCountries } from '../../services/firestoreService';
import { submitPIF, encodeFileForUpload } from '../../services/pifService';
import PIFQuestionRenderer from './PIFQuestionRenderer';
import { SECTOR_OPTIONS, formatShortId } from '../../utils/formatters';
import {
  deriveCagrFiscalYears, deriveFutureCagrFiscalYears,
} from '../../utils/cagrAnchor';

// Fix 2 Stage A — reporting currency options for the PIF form
const REPORTING_CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'GHS', label: 'GHS — Ghanaian Cedi' },
  { value: 'NGN', label: 'NGN — Nigerian Naira' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'XOF', label: 'XOF — West African CFA Franc' },
  { value: 'KES', label: 'KES — Kenyan Shilling' },
  { value: 'ZAR', label: 'ZAR — South African Rand' },
  { value: 'EGP', label: 'EGP — Egyptian Pound' },
  { value: 'ETB', label: 'ETB — Ethiopian Birr' },
  { value: 'TZS', label: 'TZS — Tanzanian Shilling' },
  { value: 'UGX', label: 'UGX — Ugandan Shilling' },
];

export default function PIFForm({ fund, stream, rubric, prefillData, onSuccess, dealId, reopenToken }) {
  const [countries, setCountries] = useState([]);
  const [company, setCompany] = useState(prefillData?.company || {
    company_name: '',
    company_country: '',
    contact_name: '',
    contact_title: '',
    contact_email: '',
    sector: '',
    sub_sector: '',
    is_women_led: '',  // 'true' | 'false' | '' (empty = not answered)
    requested_amount: '',
    requested_currency: '',
    reporting_currency: '',  // Fix 2 Stage A — currency of all figures in this form
    most_recent_financial_year: '',  // CAGR anchor — most recent COMPLETED financial year
  });
  const [responses, setResponses] = useState(prefillData?.responses || {});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [globalError, setGlobalError] = useState('');

  useEffect(() => {
    getAllCountries().then(list => setCountries(list.filter(c => c.is_active !== false)));
  }, []);

  const currencyOptions = countries.map(c => ({
    value: c.currency_code,
    label: `${c.currency_code} — ${c.currency_name}`,
  }));

  const countryOptions = countries.map(c => ({ value: c.id, label: c.name }));

  // CAGR anchor — the year the company enters here dates every revenue slot
  // (anchor, anchor-1, …). We warn, but do NOT reject, if it looks not-yet-closed:
  // the FX registry's status:'closed' flag + the open-year drop remain the single
  // source of truth for closed vs open, so an open year is simply trimmed at scoring.
  const currentYear = new Date().getFullYear();
  const anchorYearNum = /^\d{4}$/.test(String(company.most_recent_financial_year).trim()) ?
    parseInt(company.most_recent_financial_year, 10) : null;
  const anchorNotYetClosed = anchorYearNum != null && anchorYearNum >= currentYear;

  // Derived fiscal year per CAGR_USD revenue slot, keyed by question_id. Derived
  // in render (from rubric.cagr_usd_slots + the anchor), so labels update LIVE as
  // the company types the anchor. Empty until a valid 4-digit anchor exists →
  // slots keep their generic labels; a wrong or blank year is never shown.
  const slotFiscalYears = {};
  if (anchorYearNum != null) {
    for (const slot of (rubric?.cagr_usd_slots || [])) {
      try {
        Object.assign(slotFiscalYears, deriveCagrFiscalYears({
          year_question_ids: slot.year_question_ids,
          anchor: anchorYearNum,
        }));
      } catch {
        // Anchor not usable — fall back to generic labels, never guess a year.
      }
    }
  }

  // FORECAST slots for a CAGR_USD_FUTURE question: the forward series runs from
  // A+1 positionally (slot i -> A+1+i), so labels are marked "(projected)" and a
  // company cannot mistake a forecast slot for a historical one. Same live-update
  // and generic-fallback behaviour as the historical slots above. A question is
  // never in both maps — forecast questions are distinct from historical ones.
  const slotFutureFiscalYears = {};
  if (anchorYearNum != null) {
    for (const slot of (rubric?.cagr_usd_future_slots || [])) {
      try {
        const years = deriveFutureCagrFiscalYears({
          slot_count: (slot.year_question_ids || []).length,
          anchor: anchorYearNum,
        });
        (slot.year_question_ids || []).forEach((qid, i) => {
          if (qid) slotFutureFiscalYears[qid] = years[i];
        });
      } catch {
        // Anchor not usable — fall back to generic labels, never guess a year.
      }
    }
  }

  function setComp(field, value) {
    setCompany(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function setResponse(questionId, value) {
    setResponses(prev => ({ ...prev, [questionId]: value }));
    if (errors[`q_${questionId}`]) setErrors(prev => ({ ...prev, [`q_${questionId}`]: '' }));
  }

  function validate() {
    const e = {};
    if (!company.company_name?.trim()) e.company_name = 'Company name is required';
    if (!company.company_country) e.company_country = 'Country is required';
    if (!company.contact_name?.trim()) e.contact_name = 'Contact name is required';
    if (!company.contact_title?.trim()) e.contact_title = 'Job title is required';
    if (!company.contact_email?.trim()) e.contact_email = 'Email is required';
    if (company.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(company.contact_email)) {
      e.contact_email = 'Enter a valid email address';
    }
    if (!company.sector) e.sector = 'Sector is required';
    if (!company.reporting_currency) e.reporting_currency = 'Please select the currency you are reporting in';
    // CAGR anchor: mandatory, 4-digit, plausible. A current/unclosed year is
    // ACCEPTED (warned inline, not blocked) — decision (i).
    const anchorRaw = String(company.most_recent_financial_year || '').trim();
    if (!anchorRaw) {
      e.most_recent_financial_year = 'Please enter your most recent completed financial year';
    } else if (!/^\d{4}$/.test(anchorRaw)) {
      e.most_recent_financial_year = 'Enter a 4-digit year, e.g. 2025';
    } else if (parseInt(anchorRaw, 10) < 1990 || parseInt(anchorRaw, 10) > currentYear + 1) {
      e.most_recent_financial_year = 'Enter a valid financial year';
    }
    if (company.is_women_led === '') e.is_women_led = 'Please answer this question';
    if (!company.requested_amount) e.requested_amount = 'Requested amount is required';
    if (!company.requested_currency) e.requested_currency = 'Currency is required';

    // Derived questions are never shown to applicants — skip them in validation
    (rubric?.questions || []).filter(q => !q.is_derived).forEach(q => {
      if (q.is_required) {
        const r = responses[q.question_id];
        if (!r) { e[`q_${q.question_id}`] = 'This question is required'; return; }
        if ((q.response_type === 'binary' || q.response_type === 'multiple_choice') && !r.selected_option_id) {
          e[`q_${q.question_id}`] = 'Please select an option';
        }
        if (q.response_type === 'figure' && (r.response_value === '' || r.response_value === undefined)) {
          e[`q_${q.question_id}`] = 'Please enter a value';
        }
        if (q.response_type === 'date' && !r.response_value) {
          e[`q_${q.question_id}`] = 'Please enter a date';
        }
        if ((q.response_type === 'text' || q.response_type === 'long_text') &&
            (!r.response_value || !String(r.response_value).trim())) {
          e[`q_${q.question_id}`] = 'Please enter an answer';
        }
      }
    });
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstErrorKey = Object.keys(errs)[0];
      const el = document.getElementById(`field_${firstErrorKey}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    setGlobalError('');

    try {
      // Derived questions have no applicant responses — exclude them from submission
      const visibleQuestions = (rubric?.questions || []).filter(q => !q.is_derived);
      const formattedResponses = visibleQuestions.map(q => {
        const r = responses[q.question_id] || {};
        return {
          question_id: q.question_id,
          response_value: r.response_value ?? '',
          selected_option_id: r.selected_option_id ?? null,
        };
      });

      // Encode file attachments
      const attachments = [];
      for (const q of visibleQuestions) {
        const r = responses[q.question_id];
        if (r?.file) {
          try {
            const encoded = await encodeFileForUpload(r.file);
            attachments.push({ ...encoded, question_id: q.question_id });
          } catch {
            // Non-fatal — proceed without the file
          }
        }
      }

      const result = await submitPIF({
        fundId: fund.id,
        streamId: stream?.id || null,
        companyDetails: {
          ...company,
          requested_amount: parseFloat(company.requested_amount),
          is_women_led: company.is_women_led === 'true',
          // Store the anchor as a Number (4-digit) or null — never a string.
          most_recent_financial_year: /^\d{4}$/.test(String(company.most_recent_financial_year).trim()) ?
            parseInt(company.most_recent_financial_year, 10) : null,
        },
        responses: formattedResponses,
        attachments,
        // Reopen/correction flow only — undefined on a normal submission, so
        // the backend takes the untouched create path.
        dealId,
        reopenToken,
      });

      setReferenceNumber(result.referenceNumber || formatShortId(result.dealId));
      setSubmitted(true);
      if (onSuccess) onSuccess(result);
    } catch (err) {
      setGlobalError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '48px 24px',
        background: '#fff',
        borderRadius: 10,
        border: '1px solid #e2e8f0',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1a2744', marginBottom: 8 }}>
          Application Received
        </h2>
        <p style={{ fontSize: 15, color: '#64748b', marginBottom: 24, lineHeight: 1.6, maxWidth: 480, margin: '0 auto 24px' }}>
          Thank you for submitting your application for <strong>{company.company_name}</strong>.
          Our team will review it carefully and respond within 2 weeks.
        </p>
        <div style={{
          display: 'inline-block',
          padding: '12px 24px',
          background: '#f5e9d3',
          borderRadius: 8,
          border: '1px solid #c9952a',
        }}>
          <p style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#a67d20', marginBottom: 4 }}>Reference Number</p>
          <p style={{ fontSize: 20, fontWeight: 700, color: '#1a2744', letterSpacing: 2 }}>{referenceNumber}</p>
        </div>
      </div>
    );
  }

  const fieldStyle = (key) => ({
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${errors[key] ? '#dc2626' : '#e2e8f0'}`,
    borderRadius: 6,
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  });

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: '#1a2744',
    marginBottom: 4,
  };

  const fieldWrap = { marginBottom: 16 };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {globalError && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: 6,
          color: '#dc2626',
          fontSize: 14,
          marginBottom: 20,
        }}>
          {globalError}
        </div>
      )}

      {/* Company Details Section */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        marginBottom: 20,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 20px',
          background: '#f8f9fa',
          borderBottom: '1px solid #e2e8f0',
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a2744', margin: 0 }}>Company Details</h2>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div id="field_company_name" style={{ ...fieldWrap, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Company Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input style={fieldStyle('company_name')} value={company.company_name}
                onChange={e => setComp('company_name', e.target.value)} placeholder="Legal company name" />
              {errors.company_name && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.company_name}</p>}
            </div>

            <div id="field_company_country" style={fieldWrap}>
              <label style={labelStyle}>Company Country <span style={{ color: '#dc2626' }}>*</span></label>
              <select style={fieldStyle('company_country')} value={company.company_country}
                onChange={e => setComp('company_country', e.target.value)}>
                <option value="">Select country…</option>
                {countryOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {errors.company_country && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.company_country}</p>}
            </div>

            <div id="field_reporting_currency" style={{ ...fieldWrap, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Reporting Currency <span style={{ color: '#dc2626' }}>*</span></label>
              <select style={fieldStyle('reporting_currency')} value={company.reporting_currency}
                onChange={e => setComp('reporting_currency', e.target.value)}>
                <option value="">Select reporting currency…</option>
                {REPORTING_CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                Select the currency you are using for all financial figures in this form.
                All monetary amounts (revenue, profit, investment ask etc.) should be
                reported in this currency.
              </p>
              {errors.reporting_currency && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.reporting_currency}</p>}
            </div>

            <div id="field_most_recent_financial_year" style={{ ...fieldWrap, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Most Recent Completed Financial Year <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                style={{ ...fieldStyle('most_recent_financial_year'), maxWidth: 160 }}
                value={company.most_recent_financial_year}
                onChange={e => setComp('most_recent_financial_year', e.target.value.replace(/[^\d]/g, ''))}
                placeholder="e.g. 2025"
              />
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                The last financial year your accounts are finalised for (e.g. 2025). Your revenue
                figures are dated back from this year, so please make sure it is the most recent
                <strong> completed</strong> year.
              </p>
              {anchorNotYetClosed && !errors.most_recent_financial_year && (
                <p style={{ fontSize: 12, color: '#b45309', marginTop: 4, lineHeight: 1.5 }}>
                  {anchorYearNum} may not be a completed financial year yet. If your accounts for it
                  are not finalised, enter the last year they are — otherwise it will be treated as
                  not-yet-closed when your figures are analysed.
                </p>
              )}
              {errors.most_recent_financial_year && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.most_recent_financial_year}</p>}
            </div>

            <div id="field_sector" style={fieldWrap}>
              <label style={labelStyle}>Sector <span style={{ color: '#dc2626' }}>*</span></label>
              <select style={fieldStyle('sector')} value={company.sector}
                onChange={e => setComp('sector', e.target.value)}>
                <option value="">Select sector…</option>
                {SECTOR_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.sector && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.sector}</p>}
            </div>

            <div id="field_sub_sector" style={fieldWrap}>
              <label style={labelStyle}>Sub-sector <span style={{ color: '#64748b', fontWeight: 400 }}>(optional)</span></label>
              <input style={fieldStyle('sub_sector')} value={company.sub_sector}
                onChange={e => setComp('sub_sector', e.target.value)} placeholder="e.g. Fintech" />
            </div>

            <div id="field_is_women_led" style={{ ...fieldWrap, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>
                Is this company majority women-owned or women-led?{' '}
                <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: 24, marginTop: 6 }}>
                {[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }].map(opt => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                    <input
                      type="radio"
                      name="is_women_led"
                      value={opt.value}
                      checked={company.is_women_led === opt.value}
                      onChange={e => setComp('is_women_led', e.target.value)}
                      style={{ width: 16, height: 16 }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              {errors.is_women_led && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.is_women_led}</p>}
            </div>

            <div id="field_contact_name" style={fieldWrap}>
              <label style={labelStyle}>Contact Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input style={fieldStyle('contact_name')} value={company.contact_name}
                onChange={e => setComp('contact_name', e.target.value)} placeholder="Full name" />
              {errors.contact_name && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.contact_name}</p>}
            </div>

            <div id="field_contact_title" style={fieldWrap}>
              <label style={labelStyle}>Job Title <span style={{ color: '#dc2626' }}>*</span></label>
              <input style={fieldStyle('contact_title')} value={company.contact_title}
                onChange={e => setComp('contact_title', e.target.value)} placeholder="e.g. CEO" />
              {errors.contact_title && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.contact_title}</p>}
            </div>

            <div id="field_contact_email" style={{ ...fieldWrap, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Contact Email <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="email" style={fieldStyle('contact_email')} value={company.contact_email}
                onChange={e => setComp('contact_email', e.target.value)} placeholder="email@company.com" />
              {errors.contact_email && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.contact_email}</p>}
            </div>

            <div id="field_requested_amount" style={fieldWrap}>
              <label style={labelStyle}>Requested Investment Amount <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="number" style={fieldStyle('requested_amount')} value={company.requested_amount}
                onChange={e => setComp('requested_amount', e.target.value)} placeholder="0" min="0" />
              {errors.requested_amount && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.requested_amount}</p>}
            </div>

            <div id="field_requested_currency" style={fieldWrap}>
              <label style={labelStyle}>Currency <span style={{ color: '#dc2626' }}>*</span></label>
              <select style={fieldStyle('requested_currency')} value={company.requested_currency}
                onChange={e => setComp('requested_currency', e.target.value)}>
                <option value="">Select currency…</option>
                {currencyOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {errors.requested_currency && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.requested_currency}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Rubric Questions Section */}
      {rubric?.questions?.length > 0 && (
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          marginBottom: 24,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 20px', background: '#f8f9fa', borderBottom: '1px solid #e2e8f0' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a2744', margin: 0 }}>Application Questions</h2>
          </div>
          <div style={{ padding: 20 }}>
            {rubric.questions
              .filter(q => !q.is_derived)
              .slice()
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(q => (
                <div key={q.question_id} id={`field_q_${q.question_id}`}>
                  <PIFQuestionRenderer
                    question={q}
                    value={responses[q.question_id]}
                    onChange={setResponse}
                    error={errors[`q_${q.question_id}`]}
                    fiscalYearLabel={slotFutureFiscalYears[q.question_id] ?
                      `FY${slotFutureFiscalYears[q.question_id]} (projected)` :
                      slotFiscalYears[q.question_id] ?
                        `FY${slotFiscalYears[q.question_id]}` : undefined}
                  />
                </div>
              ))}
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        style={{
          width: '100%',
          padding: '14px 24px',
          background: submitting ? '#a67d20' : '#c9952a',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 16,
          fontWeight: 600,
          cursor: submitting ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          transition: 'background 0.15s',
        }}
      >
        {submitting ? (
          <>
            <span style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
            Submitting…
          </>
        ) : 'Submit Application'}
      </button>
    </form>
  );
}
